import { useCallback, useRef } from 'react';
import type { EmitInput } from './session';

/**
 * The visibility-gated exposure emitter, shared by every reading surface (M6a step 5).
 *
 * Watch has carried this rule since M2 and the story reader needs the same one, so it is written
 * once here instead of twice: exposure is only claimed for material the learner was actually
 * looking at, and each unit of material claims it once.
 *
 * Two things the gate does, and one it deliberately does not:
 *
 *  - **The document-visibility gate.** A backgrounded tab keeps playing audio and keeps firing
 *    the callbacks that drive this, and a phone in a pocket is not a learner reading. A hidden
 *    tab therefore emits nothing AND records nothing — the same line, seen later, is still that
 *    learner's first look at it.
 *  - **Once per key.** `key` is the line or the sentence; the set is what stops a re-render, a
 *    repeated observer callback, or a scroll back up the page from re-claiming the same words.
 *  - It does **not** decide what a row means. Callers build their own rows: Watch emits two per
 *    token (`listen` and `read`), the reader emits one per tagged item with its dwell. The gate
 *    is the shared part; the vocabulary is not.
 *
 * `rearm()` clears the set. Watch calls it on a backward seek over 1.5 s so a replayed line
 * counts again — for a linear playthrough a set and Watch's original single-slot ref behave
 * identically, and for the reader (where the learner scrolls freely in both directions) only the
 * set is right.
 */
export interface ExposureGate {
  /** True the first time this key is claimed on a visible page — and only then. */
  claim(key: string): boolean;
  /** Forget every claim: a replay is a new exposure. */
  rearm(): void;
}

export function createExposureGate(deps: { visible?: () => boolean } = {}): ExposureGate {
  const visible =
    deps.visible ??
    (() => typeof document === 'undefined' || document.visibilityState === 'visible');
  let seen = new Set<string>();
  return {
    claim(key) {
      if (!visible()) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    },
    rearm() {
      seen = new Set();
    },
  };
}

export interface ExposureEmitter {
  /** Emit `rows` if this key has not been claimed and the tab is on screen. */
  expose(key: string, rows: EmitInput[]): void;
  rearm(): void;
}

/**
 * The gate as a screen uses it: one per mount, stable across renders.
 *
 * `deps` is read ONCE, when the gate is built — a later render's object is ignored, so the caller
 * may pass a literal. Watch wants the default (the playhead emits whether or not anyone is
 * looking, so the document gate is the only thing standing between a pocketed phone and a claimed
 * exposure). A surface that has already measured attention — the reader, whose rows exist only
 * because a sentence was on screen for a measured stretch — passes `visible: () => true`, because
 * checking the document at EMIT time would drop exactly the rows that are settled as the page
 * goes away.
 */
export function useExposure(
  emit: (input: EmitInput) => unknown,
  deps: { visible?: () => boolean } = {},
): ExposureEmitter {
  const gateRef = useRef<ExposureGate | null>(null);
  gateRef.current ??= createExposureGate(deps);
  const expose = useCallback(
    (key: string, rows: EmitInput[]) => {
      if (!gateRef.current?.claim(key)) return;
      for (const row of rows) emit(row);
    },
    [emit],
  );
  const rearm = useCallback(() => gateRef.current?.rearm(), []);
  return { expose, rearm };
}
